import { useQuery } from '@apollo/client';
import {
  Course_DifficultyLevel as CourseDifficultyLevel,
  DescriptionPage_DifficultyLevel as DifficultyLevel,
} from '__generated__/graphql-types';
import type { Partner_Partner as Partner } from '__generated__/graphql-types';

import { useIntl } from 'bundles/page/lib/useIntl';
import CourseProductCardsQuery from 'bundles/unified-description-page-common/queries/CourseProductCardsQuery.graphql';
import SpecializationProductCardsQuery from 'bundles/unified-description-page-common/queries/SpecializationProductCardsQuery.graphql';
import type {
  CourseProductCardsQueryQuery,
  CourseProductCardsQueryQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/CourseProductCardsQuery';
import type {
  SpecializationProductCardsQueryQuery,
  SpecializationProductCardsQueryQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/SpecializationProductCardsQuery';

import _t from 'i18n!nls/xdp';

export type ProductFromQuery = {
  id: string;
  name: string;
  slug: string;
  partners: Array<Pick<Partner, 'squareLogo' | 'name' | 'id'>>;
  difficultyLevel?: DifficultyLevel;
  totalLearningDuration?: string;
  cardImageUrl?: string;
  coBrandingEnabled?: boolean;
};

export const mapToDescriptionPageDifficultyLevel = (difficultyLevel?: CourseDifficultyLevel | null) => {
  switch (difficultyLevel) {
    case CourseDifficultyLevel.Beginner:
      return DifficultyLevel.Beginner;
    case CourseDifficultyLevel.Intermediate:
      return DifficultyLevel.Intermediate;
    case CourseDifficultyLevel.Advanced:
      return DifficultyLevel.Advanced;
    default:
      return undefined;
  }
};

export const useSpecializationProductCardsQuery = ({
  ids,
  slugs,
}: {
  ids: Array<string>;
  slugs: Array<string>;
}): { loading: boolean; productCards: ProductFromQuery[] } => {
  const intl = useIntl();

  const { loading, data } = useQuery<
    SpecializationProductCardsQueryQuery,
    SpecializationProductCardsQueryQueryVariables
  >(SpecializationProductCardsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      ids,
      slugs,
    },
    skip: !ids.length && !slugs.length,
    context: { clientName: 'gatewayGql' },
    ssr: false,
  });

  const productCards = [...(data?.Specialization?.queryByIds ?? []), ...(data?.Specialization?.queryBySlugs ?? [])].map(
    (product) => {
      return {
        id: product.id,
        name: product.name,
        slug: product.slug,
        partners: product.partners,
        difficultyLevel: product.difficultyLevel,
        totalLearningDuration: product.monthsToCompleteOverride
          ? intl.formatMessage(_t('{totalMonths} {totalMonths, plural, =1 {month} other {months}}'), {
              totalMonths: product.monthsToCompleteOverride,
            })
          : '',
        cardImageUrl: product.cardImageUrl,
        coBrandingEnabled: product.coBrandingEnabled,
      };
    }
  );

  return {
    loading,
    productCards,
  };
};

export const useCourseProductCardsQuery = ({
  ids,
  slugs,
}: {
  ids: Array<string>;
  slugs: Array<string>;
}): { loading: boolean; productCards: ProductFromQuery[] } => {
  const { loading, data } = useQuery<CourseProductCardsQueryQuery, CourseProductCardsQueryQueryVariables>(
    CourseProductCardsQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        ids,
        slugs,
      },
      skip: !ids.length && !slugs.length,
      context: { clientName: 'gatewayGql' },
      ssr: false,
    }
  );

  const productCards = [...(data?.Course?.queryByIds ?? []), ...(data?.Course?.queryBySlugs ?? [])].map((product) => {
    return {
      id: product.id,
      name: product.name,
      slug: product.slug,
      partners: product.partners,
      difficultyLevel: mapToDescriptionPageDifficultyLevel(product.difficultyLevel),
    };
  });

  return {
    loading,
    productCards,
  };
};
